import { computed, unref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export const INBOX_FEATURES = {
  REPLY_TO: 'replyTo',
  REPLY_TO_OUTGOING: 'replyToOutgoing',
};

export const INBOX_FEATURE_MAP = {
  [INBOX_FEATURES.REPLY_TO]: [
    INBOX_TYPES.FB,
    INBOX_TYPES.WEB,
    INBOX_TYPES.TWITTER,
    INBOX_TYPES.WHATSAPP,
    INBOX_TYPES.TELEGRAM,
    INBOX_TYPES.TIKTOK,
    INBOX_TYPES.API,
  ],
  [INBOX_FEATURES.REPLY_TO_OUTGOING]: [
    INBOX_TYPES.WEB,
    INBOX_TYPES.TWITTER,
    INBOX_TYPES.WHATSAPP,
    INBOX_TYPES.TELEGRAM,
    INBOX_TYPES.TIKTOK,
    INBOX_TYPES.API,
  ],
};

export function useInbox(inboxPayload) {
  const store = useStore();

  const inbox = computed(() => {
    let payload = unref(inboxPayload);

    if (!payload && typeof payload !== 'number') {
      const chat = store.getters.getSelectedChat;
      if (chat) {
        payload = chat.inbox_id;
      }
    }

    let result = payload;
    if (typeof payload === 'number' || typeof payload === 'string') {
      result = store.getters['inboxes/getInboxById'](payload);
    }
    return useCamelCase(result) || {};
  });

  const channelType = computed(() => inbox.value.channelType);

  const whatsAppAPIProvider = computed(() => inbox.value.provider || '');

  const isAnEmailChannel = computed(
    () => channelType.value === INBOX_TYPES.EMAIL
  );

  const isAMicrosoftInbox = computed(
    () => isAnEmailChannel.value && inbox.value.provider === 'microsoft'
  );

  const isAGoogleInbox = computed(
    () => isAnEmailChannel.value && inbox.value.provider === 'google'
  );

  const isAPIInbox = computed(() => channelType.value === INBOX_TYPES.API);

  const isATwitterInbox = computed(
    () => channelType.value === INBOX_TYPES.TWITTER
  );

  const isAFacebookInbox = computed(() => channelType.value === INBOX_TYPES.FB);

  const isAWebWidgetInbox = computed(
    () => channelType.value === INBOX_TYPES.WEB
  );

  const isATwilioChannel = computed(
    () => channelType.value === INBOX_TYPES.TWILIO
  );

  const isALineChannel = computed(() => channelType.value === INBOX_TYPES.LINE);

  const isAVoiceChannel = computed(
    () => channelType.value === INBOX_TYPES.VOICE
  );

  const isATelegramChannel = computed(
    () => channelType.value === INBOX_TYPES.TELEGRAM
  );

  const isATwilioSMSChannel = computed(() => {
    const { medium = '' } = inbox.value;
    return isATwilioChannel.value && medium === 'sms';
  });

  const isASmsInbox = computed(
    () => channelType.value === INBOX_TYPES.SMS || isATwilioSMSChannel.value
  );

  const isATwilioWhatsAppChannel = computed(() => {
    const { medium = '' } = inbox.value;
    return isATwilioChannel.value && medium === 'whatsapp';
  });

  const isAWhatsAppCloudChannel = computed(() => {
    return (
      channelType.value === INBOX_TYPES.WHATSAPP &&
      whatsAppAPIProvider.value === 'whatsapp_cloud'
    );
  });

  const is360DialogWhatsAppChannel = computed(() => {
    return (
      channelType.value === INBOX_TYPES.WHATSAPP &&
      whatsAppAPIProvider.value === 'default'
    );
  });

  const isAWhatsAppChannel = computed(() => {
    return (
      channelType.value === INBOX_TYPES.WHATSAPP ||
      isATwilioWhatsAppChannel.value
    );
  });

  const isAnInstagramChannel = computed(
    () => channelType.value === INBOX_TYPES.INSTAGRAM
  );

  const isATiktokChannel = computed(
    () => channelType.value === INBOX_TYPES.TIKTOK
  );

  const inboxHasFeature = feature => {
    return INBOX_FEATURE_MAP[feature]?.includes(channelType.value) ?? false;
  };

  return {
    inbox,
    channelType,
    whatsAppAPIProvider,
    isAMicrosoftInbox,
    isAGoogleInbox,
    isAPIInbox,
    isATwitterInbox,
    isAFacebookInbox,
    isAWebWidgetInbox,
    isATwilioChannel,
    isALineChannel,
    isAVoiceChannel,
    isAnEmailChannel,
    isATelegramChannel,
    isATwilioSMSChannel,
    isASmsInbox,
    isATwilioWhatsAppChannel,
    isAWhatsAppCloudChannel,
    is360DialogWhatsAppChannel,
    isAWhatsAppChannel,
    isAnInstagramChannel,
    isATiktokChannel,
    inboxHasFeature,
  };
}
